import type { ComponentType, ReactNode } from 'react'
import { RequirePermission } from '@/shared/components/require-permission'

/**
 * Wraps a page in {@link RequirePermission} using JSX so both TypeScript and
 * `react/no-children-prop` are satisfied (createElement + required `children`
 * props conflict with that ESLint rule).
 */
export function createPermissionGuardedPage(
  perm: string | readonly string[],
  Page: ComponentType,
  options?: {
    mode?: 'all' | 'any'
    administratorBypass?: boolean
  },
): ComponentType {
  const mode = options?.mode ?? 'any'
  const administratorBypass = options?.administratorBypass ?? true

  return function PermissionGuardedPage() {
    return (
      <RequirePermission perm={perm} mode={mode} administratorBypass={administratorBypass}>
        <Page />
      </RequirePermission>
    )
  }
}

type ChildrenGuard = ComponentType<{ children: ReactNode }>

/** Same pattern for feature-specific guards (PRO workspace, etc.). */
export function createComponentGuardedPage(
  Guard: ChildrenGuard,
  Page: ComponentType,
): ComponentType {
  return function ComponentGuardedPage() {
    return (
      <Guard>
        <Page />
      </Guard>
    )
  }
}
